import { ref, computed, watch } from 'vue'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { auditLogger } from '@/utils/auditLogger'
import { VALID_ROLES } from '@/config/roles'
import { useAuthStore } from '@/store/authStore'
import { useSchoolStore } from '@/store/schoolStore'
import { systemSettingsWriteTarget, usesSchoolOverride } from '@/utils/schoolSystemSettings'

// Global state for student-page hamburger menu visibility.
const navVisibility = ref({})
const navVisibilityLoaded = ref(false)

// Nav items whose per-role visibility is admin-configurable.
export const NAV_VISIBILITY_ITEMS = [
  {
    key: 'aide-schedules',
    label: 'Aide Schedule',
    description: 'Show Aide Schedule in the ☰ menu (same destination as Master Schedule)'
  },
  {
    key: 'master-schedule',
    label: 'Master Schedule',
    description: 'Show Aide Schedule in the ☰ menu (school-wide aide grid)'
  }
]

// Roles whose visibility is configurable. `admin` and `district_admin` are
// implicitly all-access (see canSeeNav) and therefore not configurable.
export const NAV_CONFIGURABLE_ROLES = VALID_ROLES.filter(
  (role) => role !== 'admin' && role !== 'district_admin'
)

// Admin-tier roles that get these menu entries by default.
const DEFAULT_VISIBLE_ROLES = ['school_admin', 'admin_504', 'sped_chair', 'sped_chair_all']

export function getDefaultNavVisibility() {
  const result = {}
  for (const item of NAV_VISIBILITY_ITEMS) {
    result[item.key] = {}
    for (const role of NAV_CONFIGURABLE_ROLES) {
      result[item.key][role] = DEFAULT_VISIBLE_ROLES.includes(role)
    }
  }
  return result
}

function mergeNavVisibility(districtStored, schoolStored) {
  const defaults = getDefaultNavVisibility()
  const merged = {}
  for (const item of NAV_VISIBILITY_ITEMS) {
    merged[item.key] = {
      ...defaults[item.key],
      ...(districtStored?.[item.key] || {}),
      ...(schoolStored?.[item.key] || {})
    }
  }
  return merged
}

let schoolWatchStarted = false

export function useNavVisibility() {
  const loadNavVisibility = async ({ strict = false } = {}) => {
    try {
      const docSnap = await getDoc(doc(db, 'app_settings', 'nav_visibility'))
      let schoolStored = null
      try {
        const user = useAuthStore().currentUser
        const schoolId = useSchoolStore().currentSchoolId
        if (usesSchoolOverride(user) && schoolId) {
          const schoolSnap = await getDoc(doc(db, 'schools', schoolId))
          schoolStored = schoolSnap.exists() ? schoolSnap.data()?.navVisibility || null : null
        }
      } catch (error) {
        if (strict) throw error
        schoolStored = null
      }
      navVisibility.value = mergeNavVisibility(
        docSnap.exists() ? docSnap.data() : null,
        schoolStored
      )
      navVisibilityLoaded.value = true
      return { ok: true }
    } catch (error) {
      console.error('Error loading nav visibility:', error)
      navVisibility.value = getDefaultNavVisibility()
      navVisibilityLoaded.value = true
      return { ok: false, error }
    }
  }

  if (!schoolWatchStarted) {
    schoolWatchStarted = true
    try {
      watch(
        () => useSchoolStore().currentSchoolId,
        (id, prev) => {
          if (!navVisibilityLoaded.value || id === prev) return
          loadNavVisibility()
        }
      )
    } catch {
      schoolWatchStarted = false
    }
  }

  const saveNavVisibility = async (value) => {
    const user = useAuthStore().currentUser
    let schoolId
    try {
      schoolId = useSchoolStore().currentSchoolId
    } catch {
      schoolId = null
    }
    const target = systemSettingsWriteTarget(user, schoolId)
    const payload = value || navVisibility.value
    try {
      if (target.scope === 'school') {
        await setDoc(
          doc(db, 'schools', target.schoolId),
          { navVisibility: payload },
          { merge: true }
        )
      } else {
        await setDoc(doc(db, 'app_settings', 'nav_visibility'), payload)
      }
    } catch (error) {
      await auditLogger.logSystemAccess(
        'nav_visibility_save_failed',
        {
          scope: target.scope,
          errorCode: error.code || 'unknown'
        },
        { schoolId: target.scope === 'school' ? target.schoolId : null }
      )
      throw error
    }
    navVisibility.value = payload
    return target
  }

  const canSeeNav = (itemKey, userRole) => {
    if (!userRole) return false
    if (userRole === 'admin' || userRole === 'district_admin') return true
    if (!navVisibilityLoaded.value) return false
    return navVisibility.value[itemKey]?.[userRole] === true
  }

  return {
    navVisibility: computed(() => navVisibility.value),
    navVisibilityLoaded: computed(() => navVisibilityLoaded.value),
    loadNavVisibility,
    saveNavVisibility,
    canSeeNav
  }
}

export default useNavVisibility
